import React from 'react';
import LocalFilterPanel from '../../../components/common/LocalFilterPanel';

const STAT_OPTIONS = [
  { id: 'min', label: 'Minimum' },
  { id: 'median', label: 'Median' },
  { id: 'max', label: 'Maximum' },
  { id: 'average', label: 'Average' },
  { id: 'count', label: 'Count' },
];

const EventMultiSelect = ({ label, hint, selected, onChange, allActivities, disabled }) => {
  const toggle = (act) => {
    if (selected.includes(act)) {
      onChange(selected.filter((a) => a !== act));
    } else {
      onChange([...selected, act]);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {hint && <span className="ml-1 text-xs text-gray-400">{hint}</span>}
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onChange(allActivities)}
            disabled={disabled || allActivities.length === 0}
            className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-40"
          >
            All
          </button>
          <button
            type="button"
            onClick={() => onChange([])}
            disabled={disabled || selected.length === 0}
            className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40"
          >
            Clear
          </button>
        </div>
      </div>
      {selected.length > 0 && (
        <p className="text-xs text-blue-600 mb-1">{selected.length} selected</p>
      )}
      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2 space-y-1 bg-white">
        {allActivities.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Load data first to see activities</p>
        ) : (
          allActivities.map((act) => (
            <label key={act} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-1 rounded">
              <input
                type="checkbox"
                checked={selected.includes(act)}
                onChange={() => toggle(act)}
                disabled={disabled}
                className="rounded"
              />
              <span className="truncate">{act}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
};

const LeadTimeStatisticsSettings = ({
  objectType,
  onObjectTypeChange,
  objectTypes,
  loading = false,
  localFilters,
  updateLocalFilter,
  activeFile,
  componentTitle,
  onTitleChange,
  startEvents = [],
  onStartEventsChange,
  endEvents = [],
  onEndEventsChange,
  activitiesForObjectType = [],
  visibleStats = ['min', 'median', 'max', 'average', 'count'],
  onVisibleStatsChange,
  showPeriod = false,
  onShowPeriodChange,
}) => {
  const toggleStat = (id) => {
    if (visibleStats.includes(id)) {
      const next = visibleStats.filter((s) => s !== id);
      if (next.length > 0) {
        onVisibleStatsChange(next);
      }
      return;
    }
    onVisibleStatsChange([...visibleStats, id]);
  };

  return (
    <div className="space-y-6">
      {/* Tile-Specific Settings */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Tile Settings</h3>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tile Title
          </label>
          <input
            type="text"
            value={componentTitle}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="Lead Time Statistics"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Object Type
          </label>
          <select
            value={objectType || ""}
            onChange={(e) => onObjectTypeChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading || objectTypes.length === 0}
          >
            {objectTypes.length === 0 && (
              <option value="">Loading types...</option>
            )}
            {objectTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Start / End Events */}
      {objectTypes.length > 0 && (
        <>
          <div className="text-xs text-gray-500 -mb-2">
            Select start and end events to measure a custom time window. Leave both empty to use the full object lifecycle.
          </div>
          <EventMultiSelect
            label="Start Events"
            hint="(earliest matching event per object)"
            selected={startEvents}
            onChange={onStartEventsChange}
            allActivities={activitiesForObjectType}
            disabled={loading}
          />
          <EventMultiSelect
            label="End Events"
            hint="(latest matching event per object)"
            selected={endEvents}
            onChange={onEndEventsChange}
            allActivities={activitiesForObjectType}
            disabled={loading}
          />
        </>
      )}

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Visible Statistics */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Visible Statistics
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {STAT_OPTIONS.map((option) => {
            const active = visibleStats.includes(option.id);
            return (
              <label
                key={option.id}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
                  active
                    ? "bg-primary-50 border-primary-300"
                    : "bg-white border-gray-300"
                } ${loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleStat(option.id)}
                  className="rounded border-gray-300 focus:ring-primary-500"
                  style={{ accentColor: "#0066cc" }}
                  disabled={loading}
                />
                <span className="text-gray-700">{option.label}</span>
              </label>
            );
          })}
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
          <input
            type="checkbox"
            checked={showPeriod}
            onChange={(e) => onShowPeriodChange(e.target.checked)}
            className="rounded"
            disabled={loading}
          />
          <span className="text-gray-700">Show covered period</span>
        </label>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Local Filter Panel - always visible */}
      <LocalFilterPanel
        localFilters={localFilters}
        updateLocalFilter={updateLocalFilter}
        activeFile={activeFile}
        disabled={loading}
        enabledFilterTypes={['dateRange', 'activityNodes', 'objectAttributes']}
      />
    </div>
  );
};

export default LeadTimeStatisticsSettings;
